/**
 * Service-worker registration and update handling.
 *
 * The rule this whole file exists to enforce: **an update never activates mid-play**.
 * `sw.js` refuses to `skipWaiting`, so a new build sits in the waiting state; this side
 * tells the player it is there and lets them take it when they choose. A game that
 * reloads itself during a shift has thrown away the shift.
 *
 * Registration is deferred until after load so it never competes with the first frame —
 * on a phone the boot is already fighting for bandwidth with the ocean textures.
 */

export interface PwaHooks {
  /** A new build is downloaded and waiting. Shown as an unobtrusive prompt, never a modal. */
  onUpdateReady: (apply: () => void) => void;
}

export function registerServiceWorker(hooks: PwaHooks): void {
  if (!('serviceWorker' in navigator)) return;
  // The dev server serves modules that must not be intercepted.
  if (import.meta.env.DEV) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').then((registration) => {
      const offerWaiting = (): void => {
        const waiting = registration.waiting;
        if (!waiting) return;
        hooks.onUpdateReady(() => {
          /*
           * The player asked for it, so it is now safe to swap. The worker takes over on
           * the next navigation; reloading here is what makes "update" mean something
           * immediately rather than at some unpredictable later moment.
           */
          waiting.postMessage({ type: 'SKIP_WAITING' });
          window.location.reload();
        });
      };

      if (registration.waiting) offerWaiting();
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          // `controller` is null on the very first install; that is not an update, it is
          // the game becoming offline-capable, and the player does not need telling.
          if (installing.state === 'installed' && navigator.serviceWorker.controller) {
            offerWaiting();
          }
        });
      });
    }).catch((err) => {
      console.warn('[deepwater] service worker registration failed', err);
    });
  });
}

/**
 * iOS suspends the AudioContext when the tab backgrounds and does not always resume it.
 *
 * Not strictly a PWA concern, but it is the same class of problem — the platform doing
 * something to a running game without asking — and it belongs next to the other one.
 */
export function wireAudioResume(resume: () => void): void {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') resume();
  });
  window.addEventListener('pageshow', resume);
}
